import moment from 'moment-timezone';
import { ApolloError } from '@apollo/client';
import { captureException } from '@/utils/sentry';
import { computeArSummary } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/utils/account-detail-ar-utils';
import { useGeneratedSalesPortalAccountDetailArQuery } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/documents/sales-portal-account-detail-ar.generated';
import { useMemo } from 'react';

interface AccountDetailARLoaderParams {
  enabled: boolean; // When false, skips the AR API request and returns zeroed balances
  restaurantUUID: string;
  supplierUUID: string;
}

interface AccountDetailARLoaderResult {
  error: ApolloError | undefined;
  loading: boolean;
  overdueBalanceInMicros: number;
  totalBalanceInMicros: number;
}

export const useAccountDetailARLoader = ({
  enabled,
  restaurantUUID,
  supplierUUID,
}: AccountDetailARLoaderParams): AccountDetailARLoaderResult => {
  // Stable across renders within the same day so the query variables object
  // doesn't change identity on every render.
  const { startOfDay, usableAfter } = useMemo(() => {
    const day = moment().startOf('day');
    return { startOfDay: day, usableAfter: day.toISOString() };
  }, []);

  const { data, error, loading } = useGeneratedSalesPortalAccountDetailArQuery({
    fetchPolicy: 'cache-and-network',
    onError: captureException,
    skip: !enabled,
    variables: {
      restaurantUUID,
      supplierUUID,
      usableAfter,
    },
  });

  const creditMemos =
    data?.getAvailableCreditMemosByCustomer?.find(
      (item) => item.restaurant_id === restaurantUUID,
    )?.credit_memos ?? [];
  const invoices =
    data?.getUnpaidOrderInvoicesByCustomer?.find(
      (item) =>
        item.restaurant_id === restaurantUUID &&
        item.supplier_id === supplierUUID,
    )?.order_invoices ?? [];

  const { overdueBalanceInMicros, totalBalanceInMicros } = computeArSummary({
    creditMemos,
    invoices,
    startOfDay,
  });

  return { error, loading, overdueBalanceInMicros, totalBalanceInMicros };
};
